import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useAppSelector } from 'hooks/useAppSelector';
import { selectEssentialWorkflowStatus } from 'routes/essential-workspace/state/form.selectors';
import { selectIsBuySgSellsTradeWay } from 'state/uiPanelState/sidePanelUi/sidePanelUi.selectors';

import { EssentialWorkflowStatus } from '../state/model';

const getTextColor = (status: EssentialWorkflowStatus, isBuySgSellsTradeWay?: boolean) => {
  switch (status) {
    case 'trade-rejected':
    case 'price-rejected':
      return 'text-danger';
    case 'price-expired':
      return 'text-warning';
    case 'priced':
      return isBuySgSellsTradeWay ? 'text-buy' : 'text-sell';
    case 'not-ready':
      return 'text-secondary';
    default:
      return 'text-primary';
  }
};

const getBorderColor = (status: EssentialWorkflowStatus, isBuySgSellsTradeWay?: boolean) => {
  switch (status) {
    case 'trade-rejected':
    case 'price-rejected':
      return 'border border-danger';
    case 'priced':
      return isBuySgSellsTradeWay ? 'border border-buy' : 'border border-sell';
    case 'price-expired':
      return 'border border-warning';
  }
};

const getBackgroundColor = (status: EssentialWorkflowStatus) => {
  switch (status) {
    case 'trade-rejected':
      return 'bg-lvl1 bg-gradient--error';
    case 'price-expired':
      return 'bg-lvl1 bg-gradient--warning';
    case 'not-ready':
      return 'bg-secondary bg-opacity-10';
    default:
      return 'bg-lvl1';
  }
};

const getShadow = (status: EssentialWorkflowStatus): string => {
  switch (status) {
    case 'priced':
      return 'shadow-float-buy';
    case 'traded':
      return 'shadow-float-traded';
    case 'price-expired':
    case 'price-rejected':
      return 'shadow-float-expired';
    default:
      return 'shadow-float';
  }
};

export function useQuoteColor(formId: string) {
  const status = useAppSelector(state => selectEssentialWorkflowStatus(state, formId));
  const isBuy = useAppSelector(state => selectIsBuySgSellsTradeWay(state, formId));

  const textColor = getTextColor(status, isBuy);
  const borderColor = getBorderColor(status, isBuy);
  const backgroundColor = getBackgroundColor(status);
  const shadow = getShadow(status);

  return {
    textColor,
    borderColor,
    backgroundColor,
    shadow,
    status,
  };
}

export function useQuote() {
  const formId = useAssertedActiveTabId();
  const colors = useQuoteColor(formId);
  return { formId, ...colors };
}
